import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    MoreVertical, Eye, EyeOff, Lock, Unlock, CheckCircle, Play, Square,
    Loader2, Copy, Link, RotateCcw, Trash2
} from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import styles from './PollCard.module.css';

const TOOLTIP_GAP = 12;
const TOOLTIP_Z = 100000;

function getQuestionPreview(activity) {
    const content = activity?.content;
    if (!content) return null;
    if (content.question != null && String(content.question).trim() !== '') {
        return String(content.question).trim();
    }
    if (activity.type === 'quiz' && Array.isArray(content.questions) && content.questions.length > 0) {
        return `Quiz: ${content.questions.length} question${content.questions.length !== 1 ? 's' : ''}`;
    }
    return null;
}

function activityHasQuestion(activity) {
    if (!activity) return false;
    if (activity.type === 'audience-qa') return true;
    const content = activity.content;
    if (!content) return false;
    if (activity.type === 'quiz') return !!content.questions?.[0]?.text?.trim();
    return !!String(content.question ?? '').trim();
}

export default function PollCard({
    activity,
    meta,
    isActive,
    isLaunched,
    isSidebarCollapsed,
    openDropdownId,
    deletingId,
    isLaunchingId,
    onSelect,
    onKeyDown,
    onToggleDropdown,
    onCopyDirectLink,
    onResetResults,
    onDuplicate,
    onDelete,
    onToggleResults,
    onToggleCorrectAnswer,
    onToggleLock,
    onLaunch,
    onStop,
    eventExpired = false,
    eventExpiredMessage = 'Event duration has expired. Extend event end date to start polls again.',
}) {
    const dropdownOpen = openDropdownId === activity.id;
    const anyDropdownOpen = openDropdownId != null;
    // An open dropdown (this card's or a neighboring card's — position: absolute means it can
    // float over adjacent cards in the list) can be covered by any hover tooltip in its path,
    // since tooltips use a much higher z-index. Every Tooltip in this file is wrapped through
    // this helper instead of used directly, so none of them can render on top of an open menu.
    // Written as a plain function (not a capitalized JSX component) deliberately — a component
    // defined inside another component's render gets a new identity every render, forcing React
    // to remount its children each time, which would itself cause the flicker this is fixing.
    const withTooltip = (tooltipProps, children) =>
        anyDropdownOpen ? children : <Tooltip {...tooltipProps}>{children}</Tooltip>;
    const questionPreview = getQuestionPreview(activity);
    const hasQuestion = activityHasQuestion(activity);
    const titleText = activity.title || meta?.label;
    const displayLabel = meta?.label || activity.type || 'Poll';
    const titleAttr = questionPreview ? `${titleText}\n\n${questionPreview}` : titleText;

    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const cardRef = useRef(null);

    const updateTooltipPos = () => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        setTooltipPos({
            left: rect.right + TOOLTIP_GAP,
            top: rect.top + rect.height / 2,
        });
    };

    useLayoutEffect(() => {
        if (!showTooltip) return;
        updateTooltipPos();
    }, [showTooltip]);

    useEffect(() => {
        if (!showTooltip) return;
        window.addEventListener('scroll', updateTooltipPos, true);
        return () => window.removeEventListener('scroll', updateTooltipPos, true);
    }, [showTooltip]);

    // Belt-and-suspenders alongside the render guard below: force this card's hover tooltip
    // closed the instant ANY dropdown opens anywhere in the sidebar (see that guard for why it
    // must be "any", not just this card's own).
    useEffect(() => {
        if (openDropdownId != null) setShowTooltip(false);
    }, [openDropdownId]);

    // An open dropdown floats (position: absolute) below its own card and physically overlaps
    // the NEXT card in the list. Moving the mouse toward a lower item like "Delete" can land
    // it inside that next card's own bounds, triggering THAT card's hover tooltip — which has
    // a very high z-index and renders on top of the dropdown, hiding/blocking whatever item was
    // under it. Checking only this card's own dropdownOpen (as before) doesn't stop that, since
    // it's a different card's tooltip doing the covering. Suppress every card's tooltip
    // whenever any dropdown in the sidebar is open at all.
    const tooltipContent = showTooltip && openDropdownId == null && (
        <div
            className={`${styles.tooltip} ${styles.tooltipPortal}`}
            role="tooltip"
            aria-hidden="true"
            style={{
                position: 'fixed',
                left: tooltipPos.left,
                top: tooltipPos.top,
                transform: 'translateY(-50%)',
                zIndex: TOOLTIP_Z,
            }}
        >
            <div className={styles.tooltipHeader}>
                <span className={styles.tooltipLabel}>{displayLabel}</span>
                <span className={styles.tooltipVotes}>{activity.response_count || 0} votes</span>
            </div>
            <div className={styles.tooltipTitle}>{titleText}</div>
            {questionPreview && (
                <div className={styles.tooltipQuestion}>{questionPreview}</div>
            )}
            <div className={styles.tooltipFooter}>
                {isActive ? 'Editing' : 'Saved'}
            </div>
            <span className={styles.tooltipArrow} aria-hidden="true" />
        </div>
    );

    const startBlockedByEventExpiry = !isLaunched && eventExpired;
    const startDisabled = isLaunchingId === activity.id || (!isLaunched && !hasQuestion) || startBlockedByEventExpiry;
    const startTooltip = isLaunched
        ? 'Stop poll'
        : startBlockedByEventExpiry
            ? eventExpiredMessage
            : !hasQuestion
                ? 'Add a question first'
                : 'Start poll';

    return (
        <div
            ref={cardRef}
            id={`activity-${activity.id}`}
            className={[
                styles.card,
                isActive ? styles.active : '',
                isLaunched ? styles.cardLive : '',
                isSidebarCollapsed ? styles.sidebarCollapsed : '',
                dropdownOpen ? styles.dropdownActive : '',
            ].filter(Boolean).join(' ')}
            onClick={() => onSelect(activity.id)}
            onKeyDown={(e) => onKeyDown(e, activity.id)}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            role="button"
            tabIndex={0}
        >
            <div className={styles.top}>
                <div className={styles.icon} style={{ '--icon-color': meta?.color, color: meta?.color }}>
                    {meta?.icon}
                </div>
                <div className={styles.info}>
                    <div className={styles.titleLine}>
                        <span className={styles.type}>{titleText}</span>
                        {isLaunched && (
                            <span className={styles.liveBadge}>
                                <span className={styles.liveDot} />
                                Live
                            </span>
                        )}
                    </div>
                    <div className={styles.meta}>
                        {isActive && !isLaunched && <span className={styles.statusDot} />}
                        <span className={styles.metaText}>{isLaunched ? 'Running' : isActive ? 'Editing' : 'Saved'}</span>
                        <span className={styles.metaSep}>·</span>
                        <span className={styles.metaVotes}>{activity.response_count || 0} votes</span>
                    </div>
                </div>

                <div className={styles.dropdownWrap}>
                    {withTooltip(
                        { title: 'More options', placement: 'top' },
                        <button
                            type="button"
                            className={styles.moreBtn}
                            onClick={(e) => { e.stopPropagation(); onToggleDropdown(activity.id); }}
                            aria-haspopup="menu"
                            aria-expanded={dropdownOpen}
                            aria-label="More options"
                        >
                            <MoreVertical size={14} />
                        </button>
                    )}

                    {dropdownOpen && (
                        <div className={styles.dropdownMenu} role="menu">
                            {(activity.response_count || 0) > 0 && (
                                <button type="button" className={styles.dropdownItem} role="menuitem" onClick={(e) => { e.stopPropagation(); onResetResults(activity.id); onToggleDropdown(null); }}>
                                    <RotateCcw size={14} /> Reset results
                                </button>
                            )}
                            {onDuplicate && (
                                <button
                                    type="button"
                                    className={styles.dropdownItem}
                                    role="menuitem"
                                    onClick={(e) => { e.stopPropagation(); onDuplicate(activity.id); onToggleDropdown(null); }}
                                >
                                    <Copy size={14} /> Duplicate
                                </button>
                            )}
                            <button
                                type="button"
                                className={`${styles.dropdownItem} ${styles.dropdownDanger}`}
                                role="menuitem"
                                onClick={(e) => { e.stopPropagation(); onDelete(activity.id, e); onToggleDropdown(null); }}
                                disabled={deletingId === activity.id}
                            >
                                {deletingId === activity.id ? <Loader2 size={14} className={styles.spinner} /> : <Trash2 size={14} />} Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.controls}>
                <div className={styles.groupLeft}>
                    <span className={styles.voteBadge}>{activity.response_count || 0} Votes</span>
                    {withTooltip(
                        { title: activity.content?.settings?.pollResults === 'hidden' ? 'Show results' : 'Hide results', placement: 'top' },
                        <button
                            type="button"
                            className={`${styles.controlBtn} ${activity.content?.settings?.pollResults === 'hidden' ? styles.controlBtnActive : ''}`}
                            onClick={(e) => { e.stopPropagation(); onToggleResults(activity); }}
                            aria-label={activity.content?.settings?.pollResults === 'hidden' ? 'Show results to participants' : 'Hide results from participants'}
                        >
                            {activity.content?.settings?.pollResults === 'hidden' ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                    )}
                    {/* Show/Hide correct answer: only for poll types that have this setting (e.g. Multiple Choice) */}
                    {activity.type === 'multiple-choice' && withTooltip(
                        { title: activity.content?.settings?.showCorrectAnswer ? 'Hide correct answer' : 'Show correct answer', placement: 'top' },
                        <button
                            type="button"
                            className={`${styles.controlBtn} ${activity.content?.settings?.showCorrectAnswer ? styles.controlBtnActive : ''}`}
                            onClick={(e) => { e.stopPropagation(); onToggleCorrectAnswer(activity); }}
                            aria-label={activity.content?.settings?.showCorrectAnswer ? 'Hide correct answer' : 'Show correct answer'}
                        >
                            <CheckCircle size={14} />
                        </button>
                    )}
                    {withTooltip(
                        { title: activity.content?.settings?.isLocked ? 'Unlock voting' : 'Lock voting', placement: 'top' },
                        <button
                            type="button"
                            className={`${styles.controlBtn} ${activity.content?.settings?.isLocked ? styles.controlBtnActive : ''}`}
                            onClick={(e) => { e.stopPropagation(); onToggleLock(activity); }}
                            aria-label={activity.content?.settings?.isLocked ? 'Unlock responses' : 'Lock responses'}
                        >
                            {activity.content?.settings?.isLocked ? <Lock size={14} /> : <Unlock size={14} />}
                        </button>
                    )}
                </div>
                <div className={styles.groupRight}>
                    {withTooltip(
                        { title: startTooltip, placement: 'top' },
                        <span style={{ display: 'inline-flex' }}>
                            <button
                                type="button"
                                className={[
                                    styles.controlBtnMain,
                                    isLaunched ? styles.controlBtnStop : '',
                                    !isLaunched && (!hasQuestion || startBlockedByEventExpiry) ? styles.controlBtnNoQuestion : '',
                                ].filter(Boolean).join(' ')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isLaunched) onStop(activity.id, e);
                                    else if (hasQuestion && !startBlockedByEventExpiry) onLaunch(activity.id, e);
                                }}
                                disabled={startDisabled}
                                aria-label={isLaunched ? 'Stop activity' : startBlockedByEventExpiry ? 'Event expired; cannot start activity' : !hasQuestion ? 'Question required to start' : 'Start activity'}
                            >
                            {isLaunchingId === activity.id ? (
                                <Loader2 size={14} className={styles.spinner} />
                            ) : isLaunched ? (
                                <Square size={14} fill="currentColor" />
                            ) : (
                                <Play size={14} fill="currentColor" />
                            )}
                            <span className={styles.controlBtnMainLabel}>
                                {isLaunchingId === activity.id ? 'Working…' : isLaunched ? 'Stop' : 'Start'}
                            </span>
                            </button>
                        </span>
                    )}
                </div>
            </div>
            {createPortal(tooltipContent, document.body)}
        </div>
    );
}
